import React, { useState, useEffect } from 'react';
import { Card, Table, Select, DatePicker, Tag, Space, Modal, message, Badge, Statistic, Row, Col, Typography } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, EyeOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import loggingApi from '../../interceptors/loggingApi';
import StandardButton from '../StandardButton';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const LogViewer = ({ application }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    level: null,
    environment: null,
    dateRange: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    if (application) {
      fetchLogs();
      fetchStatistics();
    }
  }, [application?.id, filters, pagination.current]);

  const fetchLogs = async () => {
    if (!application?.id) return;
    
    setLoading(true);
    try {
      const params = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize
      };
      
      if (filters.level) params.level = filters.level;
      if (filters.environment) params.environment = filters.environment;
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].toISOString();
        params.endDate = filters.dateRange[1].toISOString();
      }
      
      const response = await loggingApi.get(`/applications/${application.id}/logs`, { params });
      setLogs(response.data.logs);
      setPagination({
        ...pagination,
        total: response.data.pagination.total
      });
    } catch (error) {
      message.error(error?.response?.data?.error || t('failed_to_load_logs'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    if (!application?.id) return;
    
    try {
      const response = await loggingApi.get(`/applications/${application.id}/statistics`);
      setStatistics(response.data);
    } catch (_) {
    }
  };

  const resolveError = async (logId) => {
    try {
      await loggingApi.post(`/applications/${application.id}/logs/${logId}/resolve`);
      message.success(t('error_resolved'));
      fetchLogs();
      fetchStatistics();
    } catch (error) {
      message.error(t('failed_to_resolve_error'));
    }
  };

  const viewLogDetails = async (logId) => {
    try {
      const response = await loggingApi.get(`/applications/${application.id}/logs/${logId}`);
      setSelectedLog(response.data);
      setModalVisible(true);
    } catch (error) {
      message.error(t('failed_to_load_log_details'));
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'processing';
      default: return 'default';
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'error': return <Badge status="error" />;
      case 'warning': return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'info': return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      default: return null;
    }
  };

  const columns = [
    {
      title: t('level'),
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level) => (
        <Space>
          {getLevelIcon(level)}
          <Tag color={getLevelColor(level)}>{level.toUpperCase()}</Tag>
        </Space>
      ),
    },
    {
      title: t('message'),
      dataIndex: 'message',
      key: 'message',
      render: (text, record) => (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <div style={{ fontWeight: record.level === 'error' ? 500 : 'normal' }}>
            {text.length > 200 ? `${text.substring(0, 200)}...` : text}
          </div>
          {record.metadata && Object.keys(record.metadata).length > 0 && (
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
              {Object.keys(record.metadata).slice(0, 3).map(key => (
                <Tag key={key} size="small" style={{ marginRight: 4 }}>
                  {key}: {typeof record.metadata[key] === 'object' 
                    ? JSON.stringify(record.metadata[key]).substring(0, 30)
                    : String(record.metadata[key]).substring(0, 30)}
                </Tag>
              ))}
            </div>
          )}
        </Space>
      ),
    },
    {
      title: t('environment'),
      dataIndex: 'environment',
      key: 'environment',
      width: 120,
      render: (env) => env ? <Tag>{env}</Tag> : '-',
    },
    {
      title: t('created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: t('status'),
      key: 'status',
      width: 100,
      render: (_, record) => (
        record.resolved ? (
          <Tag color="success">{t('resolved')}</Tag>
        ) : (
          record.level === 'error' && (
            <StandardButton
              type="secondary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => resolveError(record.id)}
            >
              {t('resolve')}
            </StandardButton>
          )
        )
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <StandardButton
          type="secondary"
          icon={<EyeOutlined />}
          size="small"
          aria-label={t('log_details')}
          onClick={() => viewLogDetails(record.id)}
        />
      ),
    },
  ];

  if (!application) return null;

  return (
    <div>
      {/* Statistics Cards */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title={t('unresolved_errors')}
                value={statistics.unresolved_errors}
                valueStyle={{ color: '#cf1322' }}
                prefix={<Badge status="error" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title={t('most_common_error')}
                value={statistics.top_errors?.[0]?.count || 0}
              />
              <Text
                type="secondary"
                style={{ display: 'block', marginTop: 8 }}
                ellipsis={{ tooltip: statistics.top_errors?.[0]?.message || '-' }}
              >
                {statistics.top_errors?.[0]?.message
                  ? `${t('most_common_error_message')}: ${statistics.top_errors[0].message}`
                  : '-'}
              </Text>
            </Card>
          </Col>
          <Col span={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title={t('total_errors_7d')}
                value={statistics.daily_stats?.reduce((sum, day) => sum + day.error_count, 0) || 0}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card style={{ minHeight: 130 }}>
              <Statistic
                title={t('environments')}
                value={statistics.environment_stats?.length || 0}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder={t('filter_by_level')}
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters({ ...filters, level: value })}
          >
            <Option value="error">{t('errors')}</Option>
            <Option value="warning">{t('warnings')}</Option>
            <Option value="info">{t('info')}</Option>
          </Select>
          
          <Select
            placeholder={t('filter_by_environment')}
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters({ ...filters, environment: value })}
          >
            <Option value="production">{t('production')}</Option>
            <Option value="staging">{t('staging')}</Option>
            <Option value="development">{t('development')}</Option>
          </Select>
          
          <RangePicker
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          
          <StandardButton
            type="secondary"
            icon={<ReloadOutlined />}
            onClick={() => {
              setFilters({ level: null, environment: null, dateRange: null });
              setPagination({ ...pagination, current: 1 });
            }}
          >
            {t('reset_filters')}
          </StandardButton>
        </Space>
      </Card>

      {/* Logs Table */}
      <Card title={`${t('logs')} - ${application.name}`}>
        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize })
          }}
        />
      </Card>

      {/* Log Details Modal */}
      <Modal
        title={t('log_details')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <StandardButton key="close" type="secondary" onClick={() => setModalVisible(false)}>
            {t('close')}
          </StandardButton>
        ]}
        width={800}
      >
        {selectedLog && (
          <div>
            <p><strong>{t('level')}:</strong> <Tag color={getLevelColor(selectedLog.level)}>{selectedLog.level}</Tag></p>
            <p><strong>{t('message')}:</strong> {selectedLog.message}</p>
            {selectedLog.stack && (
              <>
                <p><strong>{t('stack_trace')}:</strong></p>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, overflow: 'auto', maxHeight: 300, fontSize: 12 }}>
                  {selectedLog.stack}
                </pre>
              </>
            )}
            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <>
                <p><strong>{t('metadata')}:</strong></p>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, overflow: 'auto', maxHeight: 300, fontSize: 12 }}>
                  {JSON.stringify(selectedLog.metadata, null, 2)}
                </pre>
              </>
            )}
            <p><strong>{t('environment')}:</strong> {selectedLog.environment || '-'}</p>
            <p><strong>{t('created_at')}:</strong> {new Date(selectedLog.created_at).toLocaleString()}</p>
            <p><strong>{t('resolved')}:</strong> {selectedLog.resolved ? t('yes') : t('no')}</p>
            {selectedLog.resolved_at && (
              <p><strong>{t('resolved_at')}:</strong> {new Date(selectedLog.resolved_at).toLocaleString()}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LogViewer;